import type { QueryClient } from '@tanstack/react-query';
import { App as CapacitorApp } from '@capacitor/app';

import { authApi } from '@/api/auth';
import { useAuthStore } from '@/stores/auth';
import { isStoreBuild } from '@/lib/platform';
import { loadAppConfig } from '@/lib/appConfig';
import { applyTextOverrides } from '@/app/i18n';

/**
 * Обновление данных при возвращении к приложению.
 *
 * ЗАЧЕМ. Найдено 21.08.2026 живой проверкой: админ выдал ученику доступ к курсу,
 * а в приложении не появилось ничего. В базе всё было правильно — не обновлялся
 * экран. Совпало три вещи:
 *   • `refetchOnWindowFocus` выключен — по возвращении фокуса запросы не идут;
 *   • кэш ответов хранится НЕДЕЛЮ (это сделано ради работы без связи);
 *   • телефон приложение не перезагружает, а «будит» — экраны остаются
 *     смонтированными, и повода перезапросить данные не возникает вовсе.
 * В итоге ученик мог сутками видеть «курсов нет», хотя доступ выдан.
 *
 * Здесь одно правило: вернулись к приложению — перезапрашиваем то, что сейчас
 * на экране, и заодно самого пользователя. Второе не менее важно: набор
 * доступных возможностей приходит при входе и больше не обновлялся нигде
 * (`/auth/me` не вызывался ни из одного экрана), поэтому смена тарифа доходила
 * бы до ученика только после выхода и повторного входа.
 *
 * Перезапрашиваем ТОЛЬКО активные запросы — те, что смонтированы прямо сейчас.
 * Поднимать весь недельный кэш на мобильном интернете незачем.
 */

/** Реже раза в 20 секунд не обновляем: переключения между приложениями частые. */
const MIN_INTERVAL_MS = 20_000;

export function initFreshness(queryClient: QueryClient) {
  let lastRefresh = 0;

  const refresh = () => {
    const now = performance.now();
    if (now - lastRefresh < MIN_INTERVAL_MS) return;
    lastRefresh = now;

    // ⚠️ НАСТРОЙКИ С СЕРВЕРА — ТОЖЕ (02.10.2026). Раньше они читались один раз за запуск, а телефон приложение
    // не перезапускает, а «будит»: серверный выключатель (запись без связи, `client_id`, битрейт) доходил бы
    // до ученика только после того, как он сам смахнёт приложение. Ручка открытая, входа не требует, ответ
    // короткий; ошибку `loadAppConfig` глотает сам.
    void loadAppConfig().then(applyTextOverrides);

    // Не вошедшему обновлять нечего — и /auth/me ответит 401.
    if (!useAuthStore.getState().accessToken) return;

    void queryClient.invalidateQueries({ refetchType: 'active' });

    void authApi
      .me()
      .then((user) => useAuthStore.getState().setUser(user))
      .catch(() => {
        // Молча: истёкший токен разбирает перехватчик запросов, а обрыв связи —
        // обычное дело при возвращении к приложению, и ошибку тут показывать
        // человеку не за что.
      });
  };

  if (isStoreBuild()) {
    // Телефон: приложение «просыпается», события focus у веб-страницы может не быть.
    void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) refresh();
    });
  } else {
    // Браузер: вернулись на вкладку.
    window.addEventListener('focus', refresh);
  }

  // Возврат связи — тоже повод: пока её не было, запросы падали.
  window.addEventListener('online', refresh);
}
